// /app/frontend/src/components/AuthModal.jsx
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export const AuthModal = ({ open, onOpenChange }) => {
  const { sendMagicLink, error } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [debugLink, setDebugLink] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    const result = await sendMagicLink(email);
    setIsLoading(false);

    if (result.success) {
      setSent(true);
      // If debug_link is returned (dev mode), show it
      if (result.debugLink) {
        setDebugLink(result.debugLink);
      }
    }
  };

  const handleReset = () => {
    setSent(false);
    setEmail('');
    setDebugLink(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#00284F] dark:text-white">
            Darkocean Pile Cap Pro
          </DialogTitle>
          <DialogDescription>
            Sign in with your email to access the application
          </DialogDescription>
        </DialogHeader>

        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="auth-email-input"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#00284F] hover:bg-[#00284F]/90"
              disabled={isLoading || !email}
              data-testid="send-magic-link-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Magic Link'
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              We'll send you a secure sign-in link. No password required.
            </p>
          </form>
        ) : (
          <div className="text-center py-6 space-y-4" data-testid="magic-link-sent">
            <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Check your email</h3>
              <p className="text-muted-foreground mt-1">
                We've sent a magic link to <strong>{email}</strong>
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              Click the link in your email to sign in. The link expires in 30 minutes.
            </p>
            
            {/* Debug link for development/testing */}
            {debugLink && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                  Development Mode: Email not sent. Use this link:
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => window.location.href = debugLink}
                >
                  Click here to sign in
                </Button>
              </div>
            )}
            
            <Button variant="outline" onClick={handleReset} className="mt-4">
              Use a different email
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
